export type View = 'home' | 'results' | 'curated';

/** Curated = the top-playlists screen. Reached via /top-playlists, /playlist/*,
 *  or any URL the menu pushes that contains 'top-playlists'. Base-independent. */
export function isCuratedPath(pathname: string): boolean {
  return pathname.includes('/top-playlists') || pathname.includes('/playlist/');
}

/** Results deep-links: /similar/<seg> and /artist/<seg>. Base-independent. */
export function isResultsPath(pathname: string): boolean {
  return pathname.includes('/similar/') || pathname.includes('/artist/');
}

/** The signup / explorer-signup / affiliate-program landing routes render a modal
 *  OVER the home view (big search behind it), so they resolve to `home` even though
 *  they carry query params (promocode, utm_source, affiliate, …). Base-independent.
 *  ('/explorer-signup' is matched by its own includes — it does not contain '/signup'.) */
export function isHomeModalPath(pathname: string): boolean {
  return pathname.includes('/signup')
    || pathname.includes('/explorer-signup')
    || pathname.includes('/affiliate-program');
}

/** Query params that, on the index route, do NOT constitute a search and so keep
 *  the home view: UTM/referral tags (a UTM-only landing stays home, mirroring the
 *  UTM set in user-store.checkUtmTags) and the white-label auto-login params (which
 *  land on home and sign the user in). Any OTHER param (id_*, referenceSongIds,
 *  lyricsContains, narrative, …) is a real search → results. */
const HOME_KEEPING_PARAMS = new Set([
  'via', 'ref',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_social',
  'isWhiteLabelUser', 'em', 'ps',
]);

/** True when the query string carries at least one real search/filter param
 *  (i.e. a param that is not purely home-keeping). The leading '?' is optional. */
export function hasSearchParams(search: string): boolean {
  for (const key of new URLSearchParams(search).keys()) {
    if (!HOME_KEEPING_PARAMS.has(key)) return true;
  }
  return false;
}

/** The transient, non-URL view intent (cleared on the next navigation): the
 *  critical-error / visual-search-upload home fallback, and the visual-search
 *  send-to-main results bridge that shows results without changing the URL. */
export type ViewOverride = 'home' | 'results' | null;

/** Pure view derivation. Precedence: override → curated → home-modal → results-path
 *  → results-by-search-params → home. */
export function selectView(pathname: string, search: string, override: ViewOverride): View {
  if (override) return override;
  if (isCuratedPath(pathname)) return 'curated';
  if (isHomeModalPath(pathname)) return 'home';
  if (isResultsPath(pathname)) return 'results';
  if (hasSearchParams(search)) return 'results';
  return 'home';
}
